import React, { useContext, useMemo, useState, useRef } from "react";
import { GridGenerator } from "@actinc/dls/components";
import {
  Delete as DeleteIcon,
  DragHorizontalVariant as DragHorizontalVariantIcon,
  Plus as PlusIcon,
} from "@actinc/dls/icons/";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
  ControlledFormEditor,
  ControlledFormSelect,
  ControlledFormSwitch,
} from "../../../../components";

import { useDraggableInPortal, useStore } from "../../../../services/hooks";
import {
  getLetter,
  getTaskPermissions,
  onDragEnd,
} from "../../../../services/utils";
import { updateFoilTTSPrefixes } from "../../../../services/contentUtils";
import { filterFoilTemplatesByContentArea } from "../../../../services/templates";
import { MetadataConfigurationContext } from "../../../../metadataConfigurationProvider";

const RATIONALE_EDITOR_OPTIONS = ["sourceEditing"];

const ChoiceInteractionForm = ({
  activeTasks,
  imageAssets = [],
  index,
  interaction,
  readonly,
}) => {
  const namePrefix = `item.interactions[${index}]`;
  // console.debug(
  //   "ChoiceInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const editorsRef = useRef(new Map());

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks.item),
    [activeTasks.item],
  );

  const renderDraggable = useDraggableInPortal();

  const { control, getValues, trigger, watch, setValue } = useFormContext();
  const contentArea = useWatch({ control, name: "item.contentArea" });
  watch(`${namePrefix}.foils`);

  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const foilTemplates = useMemo(
    () =>
      filterFoilTemplatesByContentArea(metadataConfiguration?.templates)(
        contentArea,
      ),
    [metadataConfiguration, contentArea],
  );

  const {
    append,
    fields: foils,
    move,
    remove,
  } = useFieldArray({
    control,
    name: `${namePrefix}.foils`,
  });

  const maxChoicesOptions = useMemo(
    () =>
      [...Array(foils.length)]
        .map((_h, i) => i + 1)
        .map((value) => ({ label: value.toString(), value })),
    [foils.length],
  );

  const minChoicesOptions = useMemo(
    () =>
      [...Array(foils.length + 1)]
        .map((_h, i) => i)
        .map((value) => ({ label: value.toString(), value })),
    [foils.length],
  );

  const options = useMemo(() => {
    return [
      "applyTemplate",
      "content",
      "createLineRef",
      permissions?.includes("suggestChanges") && "suggestChanges",
      permissions?.includes("acceptChanges") && "acceptChanges",
    ].filter(Boolean);
  }, [permissions]);

  const itemActions = useWatch({ control, name: "item._actions" });

  const addFoil = () => {
    append({
      content: "<p></p>",
      correct: false,
      distractorRationale: "<p></p>",
    });
  };
  const deleteFoil = (i) => () => {
    remove(i);
  };

  const onReady = (i) => (ed) => {
    editorsRef.current.set(`content-${i}`, ed);
    console.info(
      `Item Interaction ${namePrefix} foil ${i} content editor is ready, %o`,
      ed,
    );
  };

  const onRationaleReady = (i) => (ed) => {
    editorsRef.current.set(`rationale-${i}`, ed);
    console.info(
      `Item Interaction ${namePrefix} foil ${i} distractorRationale editor is ready, %o`,
      ed,
    );
  };

  const handleDragStart = (start) => {
    // Temporarily disable on drag start
    editorsRef.current.forEach((editor, key) => {
      try {
        if (editor && !editor.isDestroyed && editor.isReadOnly !== undefined) {
          editor.enableReadOnlyMode("drag-operation");
        }
      } catch (error) {
        console.warn(`Error disabling editor ${key} during drag:`, error);
      }
    });
  };

  const handleDragEnd = (result) => {
    // Re-enable on drag end
    editorsRef.current.forEach((editor, key) => {
      try {
        if (editor && !editor.isDestroyed && editor.isReadOnly !== undefined) {
          editor.disableReadOnlyMode("drag-operation");
        }
      } catch (error) {
        console.warn(`Error re-enabling editor ${key} after drag:`, error);
      }
    });

    onDragEnd(move)(result);

    const { item } = getValues();
    const { elements, regenerate } = updateFoilTTSPrefixes(
      item?.accessibilityElements || [],
      item?.interactions?.[index] || {},
      getValues(`${namePrefix}.foils`),
    );

    if (regenerate) {
      setValue("item.accessibilityElements", elements, { shouldDirty: true });
      setValue("item.generateTTS", true, { shouldDirty: true });
    }
  };

  let defaultShowRationale = false;

  switch (getValues("item.contentArea")) {
    case "Math":
    case "Applied Math":
    case "Applied Mathematics":
    case "Science":
      defaultShowRationale = true;
      break;
    default:
      defaultShowRationale = false;
      break;
  }

  const [showRationale, setShowRationale] = useState(defaultShowRationale);

  if (!getValues(`${namePrefix}.identifier`)) return null;

  const handleShowRationaleChange = () => {
    setShowRationale(!showRationale);
  };

  return (
    <Box
      sx={{
        alignItems: "self-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <GridGenerator
        groups={[
          [
            {
              children: (
                <ControlledFormSelect
                  control={control}
                  defaultValue={interaction.minChoices}
                  fullWidth={true}
                  id={`${namePrefix}.minChoices`}
                  label="Minimum Choices"
                  name={`${namePrefix}.minChoices`}
                  options={minChoicesOptions}
                  readonly={
                    readonly || permissions?.includes("readonlyItemMetadata")
                  }
                  rules={{
                    required: true,
                    validate: {
                      lteCorrect: (v) =>
                        v <=
                          getValues(`${namePrefix}.foils`).filter(
                            (o) => o.correct,
                          ).length ||
                        "This field must be less than or equal to the number of correct answers",
                      lteMaxChoices: (v) =>
                        v <= getValues(`${namePrefix}.maxChoices`) ||
                        "This field must be less than or equal to the Maximum Choices",
                    },
                  }}
                  trigger={() => trigger([`${namePrefix}.maxChoices`])}
                />
              ),
              key: `${namePrefix}.minChoices`,
            },
            {
              children: (
                <ControlledFormSelect
                  control={control}
                  defaultValue={interaction.maxChoices}
                  fullWidth={true}
                  id={`${namePrefix}.maxChoices`}
                  label="Maximum Choices"
                  name={`${namePrefix}.maxChoices`}
                  options={maxChoicesOptions}
                  readonly={
                    readonly || permissions?.includes("readonlyItemMetadata")
                  }
                  rules={{
                    required: true,
                    validate: {
                      gteCorrect: (v) =>
                        v >=
                          getValues(`${namePrefix}.foils`).filter(
                            (o) => o.correct,
                          ).length ||
                        "This field must be greater than or equal to the number of correct answers",
                      gteMinChoices: (v) =>
                        v >= getValues(`${namePrefix}.minChoices`) ||
                        "This field must be greater than or equal to the Minimum Choices",
                    },
                  }}
                  trigger={() => trigger([`${namePrefix}.minChoices`])}
                />
              ),
              key: `${namePrefix}.maxChoices`,
            },
          ],
        ]}
      />
      <FormControlLabel
        control={
          <Switch
            defaultChecked={defaultShowRationale}
            id="showRationales"
            name={`showRationales`}
            onChange={handleShowRationaleChange}
          />
        }
        label="Show Rationales"
      />
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId={`${interaction.identifier}-foils`}>
          {(droppableProvided) => (
            <List
              dense={true}
              disablePadding={true}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {!!foils.length &&
                foils.map((foil, i) => {
                  const letter = getLetter(i);
                  return (
                    <Draggable
                      draggableId={foil.id}
                      index={i}
                      isDragDisabled={
                        readonly ||
                        permissions?.includes("readonlyItemMetadata")
                      }
                      key={foil.id}
                    >
                      {renderDraggable((draggableProvided) => (
                        <ListItem
                          dense={true}
                          disableGutters={true}
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                        >
                          <Box
                            sx={{
                              backgroundColor: "background.paper",
                              flexDirection: "column",
                            }}
                          >
                            <Box
                              sx={{
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{
                                  alignItems: "center",
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                {!(
                                  readonly ||
                                  permissions?.includes("readonlyItemMetadata")
                                ) && (
                                  <Box {...draggableProvided.dragHandleProps}>
                                    <DragHorizontalVariantIcon />
                                  </Box>
                                )}
                                <Typography
                                  sx={{ display: "inline-flex" }}
                                  variant="subtitle1"
                                >{`Foil ${letter}`}</Typography>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    padding: 2,
                                  }}
                                >
                                  <ControlledFormSwitch
                                    control={control}
                                    defaultValue={foil.correct}
                                    id={`${namePrefix}.foils[${i}].correct`}
                                    label="Correct"
                                    name={`${namePrefix}.foils[${i}].correct`}
                                    readonly={
                                      readonly ||
                                      permissions?.includes(
                                        "readonlyItemMetadata",
                                      )
                                    }
                                    trigger={() =>
                                      trigger([
                                        `${namePrefix}.maxChoices`,
                                        `${namePrefix}.minChoices`,
                                      ])
                                    }
                                  />
                                </Box>
                              </Box>
                              {!(
                                readonly ||
                                permissions?.includes("readonlyItemMetadata")
                              ) && (
                                <Tooltip title="Delete Foil">
                                  <IconButton
                                    aria-label={`delete foil ${letter}`}
                                    disabled={
                                      getValues(`${namePrefix}.foils`).length <=
                                      1
                                    }
                                    onClick={deleteFoil(i)}
                                    size="small"
                                    sx={{ padding: 1 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box
                              sx={{
                                pl: 4,
                                '& .ck.ck-content.ck-editor__editable[role="textbox"]':
                                  {
                                    "& figure.table": {
                                      marginLeft: "0px",
                                      "& thead tr th": {
                                        textAlign: "center",
                                        verticalAlign: "bottom",
                                      },
                                    },
                                    "& .qti-visually-hidden": {
                                      display: "none",
                                    },
                                  },
                              }}
                            >
                              {options && (
                                <ControlledFormEditor
                                  _actions={getValues("item._actions")}
                                  assets={{ images: imageAssets }}
                                  comments={`${namePrefix}.foils[${i}].contentComments`}
                                  contentArea={contentArea}
                                  control={control}
                                  defaultValue={
                                    getValues(
                                      `${namePrefix}.foils[${i}].content`,
                                    ) ||
                                    foil.content ||
                                    "<p></p>"
                                  }
                                  id={`${namePrefix}.foils[${i}].content`}
                                  key={`${foil.id}-content`}
                                  name={`${namePrefix}.foils[${i}].content`}
                                  onReady={onReady(i)}
                                  options={options}
                                  readonly={readonly}
                                  suggestions={`${namePrefix}.foils[${i}].contentSuggestions`}
                                  templates={foilTemplates}
                                />
                              )}
                            </Box>
                            {showRationale && (
                              <Box
                                sx={{
                                  pl: 9,
                                }}
                              >
                                <Typography variant="subtitle3">
                                  Distractor Rationale
                                </Typography>
                                <ControlledFormEditor
                                  _actions={itemActions}
                                  control={control}
                                  defaultValue={
                                    getValues(
                                      `${namePrefix}.foils[${i}].distractorRationale`,
                                    ) ||
                                    foil.distractorRationale ||
                                    "<p></p>"
                                  }
                                  getValues={getValues}
                                  id={`${namePrefix}.foils[${i}].distractorRationale`}
                                  key={`${foil.id}-distractorRationale`}
                                  name={`${namePrefix}.foils[${i}].distractorRationale`}
                                  onReady={onRationaleReady(i)}
                                  options={RATIONALE_EDITOR_OPTIONS}
                                  readonly={
                                    readonly ||
                                    permissions?.includes(
                                      "readonlyItemMetadata",
                                    )
                                  }
                                />
                              </Box>
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </Draggable>
                  );
                })}
              {droppableProvided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      {!(readonly || permissions?.includes("readonlyItemMetadata")) && (
        <Button
          onClick={addFoil}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Foil
        </Button>
      )}
    </Box>
  );
};

export { ChoiceInteractionForm };
